/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

import { mdiCalendarImport, mdiCalendarMonthOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import CalendarSchedulingModalError from "./CalendarSchedulingModalError";
import CalendarSchedulingModalForm from "./CalendarSchedulingModalForm";
import CalendarSchedulingModalLoading from "./CalendarSchedulingModalLoading";
import CalendarSchedulingModalSuccess from "./CalendarSchedulingModalSuccess";
import FastAPI from "../../../api/FastAPI";
import {
  API_DATE_FORMAT,
  TIME_FORMAT,
  TIME_FORMAT_SHORT,
} from "../../../constants/dateFormat";
import {
  formatTemplateOption,
  getLastTemplateUpdatedByDay,
  WeekDays,
} from "../../../utils/fast";
import { BUTTON_VARIANTS } from "../../button/Button";
import Modal from "../../modal/Modal";

import type { JSX } from "react";

import type {
  SchedulingParameters,
  Template,
} from "../../../api/FastAPI.types";
import type { ModalActions } from "../../modal/Modal";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiCalendarMonthOutline} size={1.25} />
      </div>
      <div className="text-[1.171875rem] font-semibold">{t("schedule")}</div>
    </div>
  );
}

export interface TemplateOption {
  value: string;
  label: string;
  isError: boolean;
  isEmpty: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export interface DayValue {
  active: boolean;
  template?: TemplateOption;
}

export interface FormValues {
  templateRange: {
    startDate?: Date;
    endDate?: Date;
    startTime?: string;
  };
  [WeekDays.MONDAY]: DayValue;
  [WeekDays.TUESDAY]: DayValue;
  [WeekDays.WEDNESDAY]: DayValue;
  [WeekDays.THURSDAY]: DayValue;
  [WeekDays.FRIDAY]: DayValue;
  [WeekDays.SATURDAY]: DayValue;
  [WeekDays.SUNDAY]: DayValue;
}

interface CalendarSchedulingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CalendarSchedulingModal({
  isOpen,
  onClose,
}: CalendarSchedulingModalProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const queryClient = useQueryClient();

  const { channelId } = useParams<FASTChannelRoutesParams>();

  const getTemplatesListQuery = FastAPI.generateQuery({
    fn: FastAPI.getTemplatesList,
    params: [channelId],
  });

  const { data: templatesList, isLoading: isLoadingTemplatesList } = useQuery(
    getTemplatesListQuery,
  );

  const {
    mutate: addTemplatesSchedules,
    isLoading: isLoadingTemplatesSchedules,
    isError: isErrorTemplatesSchedules,
    isSuccess: isSuccessTemplatesSchedules,
    reset: resetTemplatesSchedulesMuation,
    isIdle: isIdleTemplatesSchedules,
  } = useMutation({
    mutationFn: async (schedulingParameters: SchedulingParameters) =>
      FastAPI.templatesSchedules(channelId, schedulingParameters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCalendarFilling"] });
      queryClient.invalidateQueries({
        queryKey: ["getScheduleAllScheduleList"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCalendarFillingWeek"],
      });
    },
  });

  const getFormValues = (): FormValues => {
    const daysValue = Object.values(WeekDays).reduce(
      (acc: Record<WeekDays, DayValue>, day) => {
        const lastTemplateUpdated: Template | undefined =
          getLastTemplateUpdatedByDay(day, templatesList?.data);

        const template = lastTemplateUpdated
          ? formatTemplateOption(lastTemplateUpdated)
          : undefined;

        const active = Boolean(
          lastTemplateUpdated?.slots_errors_count === 0 &&
          lastTemplateUpdated.slots_count > 0,
        );

        acc[day] = {
          active,
          template,
        };

        return acc;
      },
      {
        [WeekDays.MONDAY]: { active: false },
        [WeekDays.TUESDAY]: { active: false },
        [WeekDays.WEDNESDAY]: { active: false },
        [WeekDays.THURSDAY]: { active: false },
        [WeekDays.FRIDAY]: { active: false },
        [WeekDays.SATURDAY]: { active: false },
        [WeekDays.SUNDAY]: { active: false },
      },
    );

    return {
      templateRange: {},
      ...daysValue,
    };
  };

  const methods = useForm<FormValues>({
    values: getFormValues(),
  });

  const formId = "calendarScheduling";

  const getIsDisabledButton = (): boolean => {
    const formValues = methods.getValues();
    const hasRange = Boolean(
      formValues.templateRange.startDate && formValues.templateRange.endDate,
    );

    const hasActiveDay = Object.values(formValues).some(
      (day) => day.active && day.template,
    );

    return !hasRange || !hasActiveDay;
  };

  const labelButton = (
    <div className="flex items-center justify-center gap-[0.46875rem]">
      <Icon path={mdiCalendarImport} size={0.9375} />
      {t("applyToCalendar")}
    </div>
  );

  const confirmModalActions: ModalActions[] = [
    {
      label: labelButton,
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      formId,
      isDisabled: getIsDisabledButton(),
    },
  ];

  const getDayTemplate = (data: FormValues, day: WeekDays): string | null => {
    // typescript error with enum
    if (data[day]?.active && data[day]?.template?.value) {
      return data[day]?.template?.value || null;
    }
    return null;
  };

  const onSubmit = (data: FormValues): void => {
    const { startDate, startTime } = data.templateRange;

    const isStartDateToday = startDate
      ? moment(startDate).isSame(moment(), "day")
      : false;

    const formatedData: SchedulingParameters = {
      monday: getDayTemplate(data, WeekDays.MONDAY),
      tuesday: getDayTemplate(data, WeekDays.TUESDAY),
      wednesday: getDayTemplate(data, WeekDays.WEDNESDAY),
      thursday: getDayTemplate(data, WeekDays.THURSDAY),
      friday: getDayTemplate(data, WeekDays.FRIDAY),
      saturday: getDayTemplate(data, WeekDays.SATURDAY),
      sunday: getDayTemplate(data, WeekDays.SUNDAY),
      period_start_date: moment(startDate).format(API_DATE_FORMAT),
      period_end_date: moment(data.templateRange.endDate).format(
        API_DATE_FORMAT,
      ),
      // The start time only applies when scheduling from the current day, so
      // the template does not override slots that have already aired today.
      ...(isStartDateToday && startTime
        ? {
            period_start_time: moment(startTime, TIME_FORMAT_SHORT).format(
              TIME_FORMAT,
            ),
          }
        : {}),
    };

    addTemplatesSchedules(formatedData);
  };

  const onCloseModal = (): void => {
    onClose();
    resetTemplatesSchedulesMuation();
  };

  return (
    <Modal
      isDivided
      actions={isIdleTemplatesSchedules ? confirmModalActions : []}
      header={<ModalHeader />}
      isOpen={isOpen}
      onClose={!isLoadingTemplatesSchedules ? onCloseModal : undefined}
      size="md"
    >
      <FormProvider {...methods}>
        {isIdleTemplatesSchedules ? (
          <CalendarSchedulingModalForm
            formId={formId}
            isLoading={isLoadingTemplatesList}
            onSubmit={methods.handleSubmit((data) => onSubmit(data))}
            templatesList={templatesList?.data ?? []}
          />
        ) : null}
        {isLoadingTemplatesSchedules ? (
          <CalendarSchedulingModalLoading />
        ) : null}
        {isSuccessTemplatesSchedules ? (
          <CalendarSchedulingModalSuccess />
        ) : null}
        {isErrorTemplatesSchedules ? <CalendarSchedulingModalError /> : null}
      </FormProvider>
    </Modal>
  );
}

export default CalendarSchedulingModal;
